import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
} from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { AuthLayout } from "../components/Layouts";
import { createLoginStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { forgotPassword } from "../api/auth";

/**
 * Forgot Password Page Component
 *
 * Handles password reset requests by collecting the user's email
 * and sending a password reset link.
 */
const ForgotPassword: React.FC = () => {
  // Styles
  const theme = useTheme();
  const loginStyles = createLoginStyles(theme);

  // Hooks
  const { isAuthenticated } = useAuthContext();

  // State
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Handle forgot password form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setStatus("");
    setIsLoading(true);

    try {
      const response = await forgotPassword(email);
      if (response.status === "passwords.sent") {
        setStatus("We have emailed your password reset link!");
      } else {
        setError(response.status);
      }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // If already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Render
  return (
    <AuthLayout>
      <title>Forgot password - Materiom Commons</title>
      <Container sx={loginStyles.container}>
        <Paper elevation={24} sx={loginStyles.paper}>
          {/* Description */}
          <Typography
            variant="body2"
            sx={{ mb: 2, color: theme.palette.text.primary, lineHeight: 1.5 }}
          >
            Forgot your password? No problem. Just let us know your email
            address and we will email you a password reset link that will allow
            you to choose a new one.
          </Typography>

          {/* Status Message */}
          {status && (
            <Alert severity="success" sx={loginStyles.errorAlert}>
              {status}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              {error}
            </Alert>
          )}

          {/* Forgot Password Form */}
          <Box component="form" onSubmit={handleSubmit}>
            {/* Email Input */}
            <Box sx={loginStyles.inputContainer}>
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={loginStyles.loginButton}
                disableRipple
              >
                {isLoading ? "Sending..." : "Email Password Reset Link"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </AuthLayout>
  );
};

export default ForgotPassword;
